import type { CellAlert } from '@liveline/ll_node_common/lib/models/websocket';

/**
 * Occurrences remembered per facility connection. One round of snapshots replays at
 * most 100 recent + 100 still-raised alerts per topic, so this covers a facility of
 * 50 cells subscribed under both topic prefixes.
 */
const SEEN_MAX = 20_000;

const occurrenceKey = (alert: CellAlert): string => `${alert.alert_id}|${alert.time_stamp}`;

/**
 * Drop alerts this client has already folded and remember the rest (LT-3368). A
 * reconnect replays every topic's buffer, and folding a replay twice is not harmless:
 * a replayed raise shares its alert_id with the row the client already folded to
 * 'cleared' and would resurrect it, and a replayed clear would count a repeat twice.
 * An occurrence is alert_id + time_stamp, so a reminder (same id, newer time_stamp)
 * still gets through and updates its row.
 */
export function takeNewCellAlerts(alerts: CellAlert[], seen: Set<string>): CellAlert[] {
    const fresh: CellAlert[] = [];
    alerts.forEach((alert) => {
        const key = occurrenceKey(alert);
        if (seen.has(key)) return;
        seen.add(key);
        fresh.push(alert);
    });
    while (seen.size > SEEN_MAX) {
        seen.delete(seen.values().next().value as string);
    }
    return fresh;
}
